import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import Departamentos from '../../../src/assets/data/departamentos.json'
import Ciudades from '../../../src/assets/data/ciudades.json'
import { tiposDeDocumento, formaPago, metodoPago, plazosPago } from '../../assets/data/arrays'; 
import OrderForm from '../forms/OrderForm';
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import toast from 'react-hot-toast';
import LoadingSpinner from '../LoadingSpinner';
import OfimaRemisionWithConsecutivos from '../OfimaRemisionWithConsecutivos';
import BoldCreateOrderModal from '../modals/BoldCreateOrderModal';
import type { OfimaConsecutivos } from '../../types/order';
import type { PagoParcial } from '../../types/orderFormTypes';
import { validateTransactionIdsForUpdate } from '../../utils/validateTransactionIdsBeforeOrderSave';
import { copyToClipboard } from '../../utils/clipboard';
import { BOLD_LINK_SAVED_BUT_ORDER_NOT_UPDATED } from '../../services/payments/boldPatchRetry';
import {
  generateAndPatchBoldLinksForPartialRows,
  retrySaveBoldPartialLinksOnOrder,
  submitPayloadNeedsPartialBoldLinks,
  type BoldPartialLinkItem,
  type BoldPartialPatchContext
} from '../../services/payments/boldPartialLinksAfterOrderSave';
import {
  extractAxiosApiErrorMessage,
  getFlexyStockInsufficientFromError,
  buildFlexyStockInsufficientToastMessage,
  isFlexyStockInsufficientError
} from '../../utils/apiErrorMessage';

interface OrderDetail {
  id: number;
  pago_verificado?: number;
  orders_id: number;
  origin_order_id: number;
  cotizacion_id?: number;
  store_name: string;
  status: string;
  total: string;
  guia?: string;
  date_created: string;
  date_modified: string;
  id_remision?: string;
  ofima_consecutivos?: OfimaConsecutivos | null;
  agente?: number;
  customer: {
    first_name: string;
    last_name: string;
    email: string;
    document?: string;
    tipo_documento?: string;
    tipo_persona?: string;
    idCuenta?: number;
    idReteFuente?: string;
    idReteICA?: string;
    PctReteIVA?: string;
  };
  billingAddress: {
    address_1: string;
    city: string;
    state: string;
    country: string;
    postcode: string;
  };
  shippingAddress: {
    phone?: string;
    address_1: string;
    city: string;
    state: string;
    country: string;
    postcode: string;
  };
  lineItems: Array<{
    product_id: string;
    product_name: string;
    quantity: number;
    price: string;
  }>;
  extraDetails?: {
    servicio_utilizado?: string;
    tipo_lead?: string;
    tipo_cliente?: string;
    transportadora?: string;
    ciudad_origen?: string;
    ciudad_destino?: string;
    banco?: string;
    conocer?: string;
    id_pago?: string;
    fuente_atribucion?: string;
    marca?: string;
    nom_remitente?: string;
    cod_regional?: string;
    cod_oficina?: string;
    cod_cuenta?: string;
    forma_pago?: string;
    dir_remitente?: string;
    tel_remitente?: string;
    ced_remitente?: string;
    currency?: string;
    payment_method?: string;
    trpcol_numguia?: string;
    customer_note?: string;
    transaction_id?: string;
    destino_pedido?: string;
    order_agente?: string;
    pagos?: PagoParcial[];
  };
}

const OrderUpdate: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [orderDetail, setOrderDetail] = useState<OrderDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [formData, setFormData] = useState<any>(null);
  const [productStockRefreshKey, setProductStockRefreshKey] = useState(0);
  const [boldPatchNotice, setBoldPatchNotice] = useState<{
    links: BoldPartialLinkItem[]
    error: string
    context: BoldPartialPatchContext
  } | null>(null);
  const [isRetryingBoldPatch, setIsRetryingBoldPatch] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const fetchOrderDetail = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`/list/${id}`);
      setOrderDetail(response.data);
      } catch (error) {
      console.error('Error obteniendo el detalle del pedido:', error);
      setError('Error al cargar los datos del pedido');
      }
    }, [id]);

    useEffect(() => {
    fetchOrderDetail();
  }, [fetchOrderDetail]);
  
  const handleSubmit = async (data: any) => {
    if (!orderDetail) return

    const currentId = Number(id)
    const txValidation = await validateTransactionIdsForUpdate(data, currentId)
    if (!txValidation.ok) {
      toast.error(txValidation.message, {
        position: 'top-center',
        duration: 4000
      })
      return
    }

    try {
      const orderData = {
        ...data
      };

      const response = await axiosInstance.put(`/list/${id}`, orderData);
      setOrderDetail(response.data);

      const orderPayload = orderData as Record<string, unknown>;
      let boldPartialMsg = '';
      if (submitPayloadNeedsPartialBoldLinks(orderPayload)) {
        const genRes = await generateAndPatchBoldLinksForPartialRows({
          orderId: currentId,
          orderDetail: response.data as Record<string, unknown>,
        });
        if (!genRes.ok && genRes.failureStep === 'bold_patch' && genRes.patchRetryContext) {
          setBoldPatchNotice({
            links: genRes.links,
            error: genRes.error || BOLD_LINK_SAVED_BUT_ORDER_NOT_UPDATED,
            context: genRes.patchRetryContext
          });
          return;
        }
        if (genRes.links.length > 0) {
          boldPartialMsg = ` ${genRes.links.length} link(s) Bold generado(s).`;
        }
        if (!genRes.ok) {
          toast.error(genRes.error || 'No se pudieron generar todos los links Bold', {
            icon: '⚠️',
            position: 'top-center',
            duration: 6000,
          });
        }
      }

      toast.success('Pedido #' + id + ' Actualizado.' + boldPartialMsg + ' 🚀', {
        position: 'top-center',
        duration: 3000
      });
      window.location.reload();
    } catch (err) {
      const flexyStock = getFlexyStockInsufficientFromError(err)
      if (flexyStock) {
        const toastMessage = buildFlexyStockInsufficientToastMessage(flexyStock, 'update')
        setError(toastMessage)
        toast(toastMessage, {
          icon: '⚠️',
          position: 'top-center',
          duration: 8000,
          style: { maxWidth: '560px', whiteSpace: 'pre-line' }
        })
        setProductStockRefreshKey((prev) => prev + 1)
        await fetchOrderDetail()
        return
      }
      console.error('Error actualizando pedido:', err);
      const errorMessage = extractAxiosApiErrorMessage(
        err,
        'Error al actualizar el pedido. Por favor, intente nuevamente.'
      );
      setError(errorMessage);
      toast.error(errorMessage, {
        icon: '⚠️',
        position: 'top-center',
        duration: 6000,
        style: { maxWidth: '520px', whiteSpace: 'pre-line' }
      });
      if (isFlexyStockInsufficientError(err)) {
        setProductStockRefreshKey((prev) => prev + 1)
      }
    }
  };

  const handleRetryBoldPatchSave = async () => {
    if (!boldPatchNotice || !id) return
    setIsRetryingBoldPatch(true)
    try {
      const res = await retrySaveBoldPartialLinksOnOrder(Number(id), boldPatchNotice.context)
      if (!res.ok) {
        setBoldPatchNotice((current) =>
          current
            ? { ...current, error: res.error || BOLD_LINK_SAVED_BUT_ORDER_NOT_UPDATED }
            : current
        )
        return
      }
      setBoldPatchNotice(null)
      toast.success('Enlaces Bold guardados en el pedido', { icon: '✅', position: 'top-center' })
      window.location.reload()
    } finally {
      setIsRetryingBoldPatch(false)
    }
  }

  const handleCopyBoldLink = async (url: string) => {
    const ok = await copyToClipboard(url)
    if (ok) toast.success('Link copiado al portapapeles', { icon: '📋', position: 'top-center' })
  }

  const closeBoldPatchNotice = () => {
    setBoldPatchNotice(null)
    window.location.reload()
  }

  if (!orderDetail) {
    return <div className="flex justify-center items-center min-h-[200px]"><LoadingSpinner size="large" /></div>;
  }

  return (
    <div className="px-10 py-5 bg-gray-50 min-h-screen mx-3">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => {
              const from = location.state?.from;
              if (from) {
                navigate(from);
              } else {
                navigate('/orders');
              }
            }} 
            className="text-blue-800 hover:text-blue-900 transition-colors">
            <ArrowLeftIcon className="h-6 w-6" />
          </button>


          <div className="items-left gap-4">
            <h3 className="">
              Editar Pedido #{orderDetail.orders_id===0?orderDetail.id:orderDetail.orders_id}  
            </h3>
            <p className="text-sm text-gray-600">
              Última actualización: {new Date(orderDetail.date_modified).toLocaleString()}
            </p>
            { orderDetail.cotizacion_id !== undefined && orderDetail.cotizacion_id > 0 && (
            <p className="text-sm text-gray-600">
             <button onClick={() => navigate(`/cotizaciones/${orderDetail.cotizacion_id}`)} className="text-blue-800 hover:text-blue-900 transition-colors"> 
               Ver cotizacion de origen #<b className="underline font-semibold">{orderDetail.cotizacion_id}</b>
             </button>
            </p>
           )}
          </div>
        </div>
          <div className="flex items-center gap-4 p-4">
            <OfimaRemisionWithConsecutivos
              idRemision={orderDetail.id_remision}
              ofimaConsecutivos={orderDetail.ofima_consecutivos ?? null}
            />
          </div>
      </div>
  
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-600 whitespace-pre-line">{error}</p>
       </div>
      )}

      <OrderForm 
        initialData={orderDetail}
        mode="edit"
        onSubmit={handleSubmit}
        onFormChange={setFormData}
        tiposDeDocumento={tiposDeDocumento}
        departamentos={Departamentos}
        ciudades={Ciudades}
        formaPago={formaPago}
        metodoPago={metodoPago}
        plazosPago={plazosPago}
        productStockRefreshKey={productStockRefreshKey}
      />

      <BoldCreateOrderModal
        isOpen={boldPatchNotice != null}
        phase="result"
        progress={{ current: 1, total: 1, percentage: 100, currentPage: 1, totalPages: 1 }}
        message=""
        paymentUrl={null}
        partialLinks={boldPatchNotice?.links ?? []}
        partialError={boldPatchNotice?.error ?? null}
        errorMessage={null}
        isRetrying={isRetryingBoldPatch}
        isCancelling={false}
        patchSavePending={boldPatchNotice != null}
        acceptLabel="Cerrar"
        onAccept={closeBoldPatchNotice}
        onRetry={() => undefined}
        onRetryPatchSave={handleRetryBoldPatchSave}
        onDismiss={closeBoldPatchNotice}
        onCopyLink={handleCopyBoldLink}
      />
    </div>
  );
};

export default OrderUpdate;
